import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRef } from 'react';
import axios from 'axios';
import { Eye, EyeOff, Package, MapPin, Clock, Shield, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import Header from '@/components/layout/Header';
import FooterNew from '@/components/layout/FooterNew';
import FeaturedBlogTeaser from '@/components/blog/FeaturedBlogTeaser';
import HeroSection from '@/components/landing/HeroSection';
import appStoreBadge from '@/assets/app-store-badge-n.svg';
import googlePlayBadge from '@/assets/google-play-badge.svg';
import { API, toast } from '@/App';
import { usePublicSeo } from '@/utils/seo';
import { UI_AUTH_LABELS } from '@/constants/uiLabels';
import { persistAuthSession } from '@/utils/authSession';

const TRUSTPILOT_SCRIPT_SRC = 'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
const TRUSTPILOT_SCRIPT_FRAGMENT = 'widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js';
const TRUSTPILOT_WIDGET_DATA = {
  locale: 'fr-FR',
  templateId: '56278e9abfbbba0bdcd568bc',
  businessUnitId: '69d6cee52411c08a3cd7c317',
  styleHeight: '52px',
  styleWidth: '100%',
  token: 'fde84e10-6fc8-485f-ab3b-4ec43a6d15a9',
  reviewUrl: 'https://fr.trustpilot.com/review/colinoki.com',
};

let trustpilotBootstrapPromise = null;
let trustpilotBootstrapStatus = 'idle';

const scheduleMicrotask = (callback) => {
  if (typeof window !== 'undefined' && typeof window.queueMicrotask === 'function') {
    window.queueMicrotask(callback);
    return;
  }
  Promise.resolve().then(callback);
};

const findExistingTrustpilotScript = () => (
  Array.from(document.querySelectorAll('script[src]')).find((node) => {
    const rawSrc = node.getAttribute('src') || '';
    return rawSrc.includes(TRUSTPILOT_SCRIPT_FRAGMENT);
  })
);

const waitForTrustpilotApi = (remainingAttempts = 24) => new Promise((resolve, reject) => {
  const checkAvailability = () => {
    if (window.Trustpilot?.loadFromElement) {
      resolve(window.Trustpilot);
      return;
    }

    if (remainingAttempts <= 0) {
      reject(new Error('Trustpilot bootstrap loaded but API is not available.'));
      return;
    }

    remainingAttempts -= 1;
    scheduleMicrotask(checkAvailability);
  };

  checkAvailability();
});

const loadTrustpilotBootstrap = () => {
  if (typeof window === 'undefined') {
    return Promise.resolve(null);
  }

  if (window.Trustpilot?.loadFromElement) {
    trustpilotBootstrapStatus = 'loaded';
    return Promise.resolve(window.Trustpilot);
  }

  if (trustpilotBootstrapStatus === 'loaded' && !window.Trustpilot?.loadFromElement) {
    trustpilotBootstrapPromise = null;
    trustpilotBootstrapStatus = 'idle';
  }

  if (trustpilotBootstrapStatus === 'loading' && !findExistingTrustpilotScript()) {
    trustpilotBootstrapPromise = null;
    trustpilotBootstrapStatus = 'idle';
  }

  if (trustpilotBootstrapPromise) {
    return trustpilotBootstrapPromise;
  }

  trustpilotBootstrapStatus = 'loading';
  trustpilotBootstrapPromise = new Promise((resolve, reject) => {
    const resolveWhenReady = () => {
      waitForTrustpilotApi()
        .then((trustpilot) => {
          trustpilotBootstrapStatus = 'loaded';
          resolve(trustpilot);
        })
        .catch(reject);
    };

    const handleScriptError = () => {
      reject(new Error('Unable to load Trustpilot bootstrap script.'));
    };

    const existingScript = findExistingTrustpilotScript();
    if (existingScript) {
      existingScript.addEventListener('load', resolveWhenReady, { once: true });
      existingScript.addEventListener('error', handleScriptError, { once: true });

      if (existingScript.dataset.trustpilotLoaded === 'true' || existingScript.readyState === 'complete') {
        resolveWhenReady();
      }
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = TRUSTPILOT_SCRIPT_SRC;
    script.async = true;
    script.dataset.trustpilotScript = 'true';
    script.addEventListener('load', () => {
      script.dataset.trustpilotLoaded = 'true';
      resolveWhenReady();
    }, { once: true });
    script.addEventListener('error', handleScriptError, { once: true });
    document.body.appendChild(script);
  }).catch((error) => {
    trustpilotBootstrapPromise = null;
    trustpilotBootstrapStatus = 'idle';
    throw error;
  });

  return trustpilotBootstrapPromise;
};

const createTrustpilotWidgetElement = () => {
  const widget = document.createElement('div');
  widget.className = 'trustpilot-widget';
  widget.setAttribute('data-locale', TRUSTPILOT_WIDGET_DATA.locale);
  widget.setAttribute('data-template-id', TRUSTPILOT_WIDGET_DATA.templateId);
  widget.setAttribute('data-businessunit-id', TRUSTPILOT_WIDGET_DATA.businessUnitId);
  widget.setAttribute('data-style-height', TRUSTPILOT_WIDGET_DATA.styleHeight);
  widget.setAttribute('data-style-width', TRUSTPILOT_WIDGET_DATA.styleWidth);
  widget.setAttribute('data-token', TRUSTPILOT_WIDGET_DATA.token);

  const anchor = document.createElement('a');
  anchor.href = TRUSTPILOT_WIDGET_DATA.reviewUrl;
  anchor.target = '_blank';
  anchor.rel = 'noopener';
  anchor.textContent = 'Trustpilot';
  widget.appendChild(anchor);

  return widget;
};

const LandingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const trustpilotWidgetRef = useRef(null);
  const isRegisterPage = location.pathname === '/register';
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [featuredBlogPost, setFeaturedBlogPost] = useState(null);
  const [referralCode, setReferralCode] = useState(() => {
    const stored = window.localStorage.getItem('cnk_referral_code');
    return stored ? String(stored).trim().toUpperCase() : '';
  });
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    phone: '',
    country: '',
    city: '',
    company_name: '',
    company_registration: ''
  });
  const [accountType, setAccountType] = useState('particulier');
  const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [mfaFlow, setMfaFlow] = useState(null);
  const [mfaCode, setMfaCode] = useState('');

  const completeLogin = (payload) => {
    persistAuthSession(payload);
    const user = payload.user;
    const normalizedRole = normalizeUserRole(user.role);
    if (user.email_verified === false) {
      toast.success('Connexion réussie. Vérifiez votre email pour continuer.');
      navigate('/verify-email', { state: { email: user.email || formData.email, role: normalizedRole } });
      return;
    }
    toast.success(`Bienvenue ${user.full_name}!`);
    navigate(resolveDashboardPath(normalizedRole));
  };

  const normalizeUserRole = (role) => {
    const normalizedRole = String(role || '').trim().toLowerCase();
    if (normalizedRole === 'courier') return 'livreur';
    if (normalizedRole === 'vendor') return 'vendeur';
    if (normalizedRole === 'agent-pr' || normalizedRole === 'agentpr' || normalizedRole === 'relay_agent') return 'agent_pr';
    if (normalizedRole === 'affiliate') return 'ambassador';
    return normalizedRole;
  };

  const resolveDashboardPath = (role) => {
    switch (normalizeUserRole(role)) {
      case 'admin':
        return '/admin';
      case 'vendeur':
        return '/vendeur';
      case 'livreur':
        return '/livreur';
      case 'agent_pr':
        return '/agent-pr';
      case 'ambassador':
      case 'super_ambassador':
        return '/affiliate/dashboard';
      default:
        return '/';
    }
  };

  usePublicSeo({
    title: isRegisterPage
      ? 'Inscription vendeur | Coli Noki'
      : 'Coli Noki | Livraison, suivi de colis et gestion vendeur',
    description: isRegisterPage
      ? 'Inscrivez votre boutique sur Coli Noki. Support du code affiliation, suivi des livraisons et accès aux options Coli Noki Pro.'
      : 'Plateforme Coli Noki pour créer des livraisons, suivre les colis et accéder aux services standard, express et assurance selon votre plan.',
    path: isRegisterPage ? '/register' : '/',
  });

  useEffect(() => {
    loadCountries();
  }, []);

  useEffect(() => {
    let active = true;

    const loadFeaturedArticle = async () => {
      try {
        const response = await axios.get(`${API}/blog/featured`);
        if (active) {
          const nextPost = response?.data && !Array.isArray(response.data) ? response.data : null;
          setFeaturedBlogPost(nextPost?.slug ? nextPost : null);
        }
      } catch (error) {
        if (active) {
          setFeaturedBlogPost(null);
        }
      }
    };

    loadFeaturedArticle();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const mountTrustpilotWidget = () => {
      const widgetContainer = trustpilotWidgetRef.current;
      if (!widgetContainer) {
        return null;
      }

      const widgetElement = createTrustpilotWidgetElement();
      widgetContainer.replaceChildren(widgetElement);
      return widgetElement;
    };

    const renderTrustpilotWidget = () => {
      scheduleMicrotask(() => {
        if (cancelled) {
          return;
        }

        const widgetElement = mountTrustpilotWidget();
        if (widgetElement && window.Trustpilot?.loadFromElement) {
          window.Trustpilot.loadFromElement(widgetElement, true);
        }
      });
    };

    mountTrustpilotWidget();

    loadTrustpilotBootstrap()
      .then(() => {
        if (!cancelled) {
          renderTrustpilotWidget();
        }
      })
      .catch(() => {
        mountTrustpilotWidget();
      });

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (location.pathname === '/register') {
      setActiveTab('register');
    }

    const params = new URLSearchParams(location.search);
    const rawRef = params.get('ref');
    if (!rawRef) {
      return;
    }

    const normalizedRef = String(rawRef).trim().toUpperCase();
    if (!normalizedRef) {
      return;
    }

    setReferralCode(normalizedRef);
    setActiveTab('register');
    window.localStorage.setItem('cnk_referral_code', normalizedRef);

    const clickTrackKey = `cnk_ref_click_tracked_${normalizedRef}_${location.pathname}`;
    if (window.sessionStorage.getItem(clickTrackKey)) {
      return;
    }

    axios
      .post(`${API}/affiliate/track-click`, {
        affiliate_code: normalizedRef,
        landing_path: location.pathname
      })
      .then(() => {
        window.sessionStorage.setItem(clickTrackKey, '1');
      })
      .catch((error) => {
        const detail = error?.response?.data?.detail;
        if (detail) {
          console.debug('Affiliate click tracking skipped:', detail);
        }
      });
  }, [location.pathname, location.search]);

  const loadCountries = async () => {
    try {
      const response = await axios.get(`${API}/countries`);
      setCountries(response.data.filter(c => c.is_active));
    } catch (error) {
      console.error('Error loading countries:', error);
    }
  };

  const handleCountryChange = (countryCode) => {
    const selectedCountry = countries.find(c => c.code === countryCode);
    setFormData({ ...formData, country: countryCode, city: '' });
    setCities(selectedCountry?.cities || []);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, {
        email: formData.email,
        password: formData.password
      });
      if (response.data?.code === 'MFA_REQUIRED') {
        setMfaFlow({ type: 'challenge', challengeToken: response.data.challenge_token });
        setMfaCode('');
        return;
      }
      if (response.data?.code === 'MFA_ENROLLMENT_REQUIRED') {
        const enrollment = await axios.post(`${API}/auth/mfa/totp/enroll/start`, {
          enrollment_token: response.data.enrollment_token,
        });
        setMfaFlow({
          type: 'enrollment',
          enrollmentToken: response.data.enrollment_token,
          enrollmentId: enrollment.data.enrollment_id,
          secret: enrollment.data.secret,
          otpauthUri: enrollment.data.otpauth_uri,
        });
        setMfaCode('');
        return;
      }
      completeLogin(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (event) => {
    event.preventDefault();
    if (!mfaFlow || !mfaCode.trim()) return;
    setLoading(true);
    try {
      const response = mfaFlow.type === 'enrollment'
        ? await axios.post(`${API}/auth/mfa/totp/enroll/confirm`, {
          enrollment_id: mfaFlow.enrollmentId,
          enrollment_token: mfaFlow.enrollmentToken,
          code: mfaCode.trim(),
        })
        : await axios.post(`${API}/auth/mfa/login/verify`, {
          challenge_token: mfaFlow.challengeToken,
          code: mfaCode.trim(),
        });
      setMfaFlow(null);
      setMfaCode('');
      completeLogin(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Code de sécurité invalide ou expiré.');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Vérifier que la politique de confidentialité est acceptée
    if (!acceptedPrivacy || !acceptedTerms) {
      toast.error("Vous devez accepter la politique de confidentialité et les conditions d'utilisation");
      return;
    }
    
    setLoading(true);

    try {
      const resolvedReferralCode = referralCode || window.localStorage.getItem('cnk_referral_code');
      const registerData = {
        email: formData.email,
        password: formData.password,
        full_name: formData.full_name,
        phone: formData.phone,
        role: 'vendeur',
        city: formData.city,
        ...(accountType === 'societe' && {
          company_name: formData.company_name,
          company_registration: formData.company_registration
        }),
        ...(resolvedReferralCode && { referral_code: String(resolvedReferralCode).trim().toUpperCase() })
      };

      const response = await axios.post(`${API}/auth/register`, registerData);
      persistAuthSession(response.data);
      window.localStorage.removeItem('cnk_referral_code');
      const createdUser = response.data?.user || {};
      const verificationQueued = response.data?.email_verification?.delivery_queued !== false;
      const createdRole = normalizeUserRole(createdUser.role || 'vendeur');

      if (createdUser.email_verified === false) {
        toast.success(
          verificationQueued
            ? (accountType === 'societe'
              ? 'Compte créé. Vérifiez votre email puis attendez la validation de votre compte.'
              : 'Compte créé. Vérifiez votre email pour activer votre compte.')
            : 'Compte créé. L’email de vérification n’a pas pu être envoyé automatiquement, vous pourrez le renvoyer.'
        );
        navigate('/verify-email', {
          state: {
            email: createdUser.email || formData.email,
            role: createdRole,
          },
        });
        return;
      }

      if (accountType === 'societe') {
        toast.success('Compte créé ! En attente de validation.');
      } else {
        toast.success('Compte créé avec succès !');
      }

      navigate(resolveDashboardPath(createdRole));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <HeroSection
        onCreateAccount={() => document.getElementById('auth-section').scrollIntoView({ behavior: 'smooth' })}
        onTrackPackage={() => navigate('/track')}
      />

      <FeaturedBlogTeaser post={featuredBlogPost} />

      {/* Features Section */}
      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-center mb-12 gradient-text">
            Pourquoi choisir Coli Noki ?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="card-hover border-t-4 border-t-red-500">
              <CardHeader>
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <Package className="w-6 h-6 text-red-600" />
                </div>
                <CardTitle>Gestion simplifiée</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Créez et suivez vos livraisons en quelques clics avec une interface intuitive
                </p>
              </CardContent>
            </Card>

            <Card className="card-hover border-t-4 border-t-cyan-500">
              <CardHeader>
                <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-4">
                  <MapPin className="w-6 h-6 text-cyan-600" />
                </div>
                <CardTitle>Suivi en temps réel</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Suivez vos colis en temps réel avec notifications à chaque étape
                </p>
              </CardContent>
            </Card>

            <Card className="card-hover border-t-4 border-t-red-500">
              <CardHeader>
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <Clock className="w-6 h-6 text-red-600" />
                </div>
                <CardTitle>Rapide et fiable</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Livraisons rapides avec notre réseau de livreurs professionnels
                </p>
              </CardContent>
            </Card>

            <Card className="card-hover border-t-4 border-t-cyan-500">
              <CardHeader>
                <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-cyan-600" />
                </div>
                <CardTitle>Sécurisé</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Vos colis sont assurés et suivis du départ à l'arrivée
                </p>
              </CardContent>
            </Card>

            <Card className="card-hover border-t-4 border-t-red-500">
              <CardHeader>
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <TrendingUp className="w-6 h-6 text-red-600" />
                </div>
                <CardTitle>Tarifs transparents</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Calcul automatique des coûts basé sur la distance et le poids
                </p>
              </CardContent>
            </Card>

            <Card className="card-hover border-t-4 border-t-cyan-500">
              <CardHeader>
                <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center mb-4">
                  <Package className="w-6 h-6 text-cyan-600" />
                </div>
                <CardTitle>Multi-villes</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Livraisons locales et inter-villes via nos Points de Relais
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Auth Section */}
      <div id="auth-section" className="bg-white pt-10 pb-20 sm:pt-12 lg:pt-14">
        <div className="mx-auto flex w-full max-w-7xl flex-col items-center px-4 sm:px-6 lg:px-8">
          <div className="mb-14 w-full max-w-5xl sm:mb-16" data-testid="landing-mobile-app-block">
            <Card className="overflow-hidden border border-red-100/80 bg-gradient-to-br from-white via-red-50 to-cyan-50 shadow-[0_18px_60px_-40px_rgba(15,23,42,0.35)]">
              <CardContent className="p-6 sm:p-8">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                  <div className="max-w-2xl space-y-3">
                    <p className="text-sm font-semibold uppercase tracking-[0.24em] text-red-500">Mobile</p>
                    <h3 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
                      Téléchargez l’application Coli Noki
                    </h3>
                    <p className="text-base leading-7 text-slate-600">
                      Suivez vos livraisons, gérez vos commandes et accédez à Coli Noki partout, directement depuis votre smartphone.
                    </p>
                  </div>

                  <div className="flex shrink-0 flex-row items-center gap-3 sm:gap-4">
                    <a
                      href="#"
                      data-testid="google-play-badge-link"
                      aria-label="Télécharger Coli Noki sur Google Play"
                      className="inline-flex max-w-[48%] transition-transform duration-200 hover:-translate-y-0.5"
                    >
                      <img
                        src={googlePlayBadge}
                        alt="Disponible sur Google Play"
                        className="h-12 w-auto sm:h-14"
                      />
                    </a>
                    <a
                      href="#"
                      data-testid="app-store-badge-link"
                      aria-label="Télécharger Coli Noki sur l’App Store"
                      className="inline-flex max-w-[48%] transition-transform duration-200 hover:-translate-y-0.5"
                    >
                      <img
                        src={appStoreBadge}
                        alt="Télécharger dans l’App Store"
                        className="h-12 w-auto sm:h-14"
                      />
                    </a>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="mb-14 w-full max-w-md sm:mb-16" data-testid="landing-auth-form-block">
            <Card className="border-2 border-red-100">
              <CardHeader>
                <CardTitle className="text-2xl text-center gradient-text">Connexion / Inscription</CardTitle>
                <CardDescription className="text-center">
                  Créez votre compte vendeur. L’inscription peut inclure une affiliation via un code invité.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="login" data-testid="login-tab">{UI_AUTH_LABELS.loginTab}</TabsTrigger>
                    <TabsTrigger value="register" data-testid="register-tab">{UI_AUTH_LABELS.registerTab}</TabsTrigger>
                  </TabsList>

                  <TabsContent value="login">
                    {mfaFlow ? (
                    <form onSubmit={handleMfaSubmit} className="space-y-4" data-testid="mfa-login-form">
                      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-slate-700">
                        <p className="font-semibold text-slate-900">
                          {mfaFlow.type === 'enrollment' ? 'Activation obligatoire de la MFA' : 'Vérification de sécurité'}
                        </p>
                        {mfaFlow.type === 'enrollment' && (
                          <>
                            <p className="mt-2">Ajoutez ce compte dans votre application Authenticator, puis saisissez le code à 6 chiffres.</p>
                            <code className="mt-2 block break-all rounded bg-white p-2" data-testid="mfa-enrollment-secret">{mfaFlow.secret}</code>
                          </>
                        )}
                      </div>
                      <div>
                        <Label htmlFor="mfa-code">Code MFA ou code de récupération</Label>
                        <Input id="mfa-code" data-testid="mfa-code" value={mfaCode} onChange={(event) => setMfaCode(event.target.value)} autoComplete="one-time-code" required />
                      </div>
                      <Button type="submit" className="w-full bg-red-500 hover:bg-red-600" disabled={loading} data-testid="mfa-submit">
                        {loading ? 'Vérification...' : 'Valider et se connecter'}
                      </Button>
                      <Button type="button" variant="ghost" className="w-full" onClick={() => setMfaFlow(null)}>Annuler</Button>
                    </form>
                    ) : (
                    <form onSubmit={handleLogin} className="space-y-4">
                      <div>
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          data-testid="login-email"
                          placeholder="votre@email.com"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="password">Mot de passe</Label>
                        <div className="relative">
                          <Input
                            id="password"
                            name="password"
                            type={showLoginPassword ? 'text' : 'password'}
                            data-testid="login-password"
                            placeholder="••••••••"
                            value={formData.password}
                            onChange={handleInputChange}
                            required
                            className="pr-10"
                          />
                          <button
                            type="button"
                            aria-label={showLoginPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                            className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-gray-500 hover:text-gray-700"
                            data-testid="login-password-toggle"
                            onClick={() => setShowLoginPassword((value) => !value)}
                          >
                            {showLoginPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        <div className="text-right mt-1">
                          <button
                            type="button"
                            onClick={() => navigate('/forgot-password')}
                            className="text-sm text-cyan-600 hover:text-cyan-700 hover:underline"
                          >
                            Mot de passe oublié ?
                          </button>
                        </div>
                      </div>
                      <Button
                        type="submit"
                        data-testid="login-submit-btn"
                        className="w-full bg-red-500 hover:bg-red-600"
                        disabled={loading}
                      >
                        {loading ? 'Connexion...' : UI_AUTH_LABELS.loginAction}
                      </Button>
                    </form>
                    )}
                  </TabsContent>

                  <TabsContent value="register">
                    <form onSubmit={handleRegister} className="space-y-4">
                      {referralCode && (
                        <div className="rounded-lg border border-cyan-200 bg-cyan-50 px-4 py-3 text-sm text-cyan-800">
                          Inscription par affiliation détectée. Code invité : <span className="font-semibold">{referralCode}</span>.
                          Le parrainage sera rattaché automatiquement après création du compte.
                        </div>
                      )}
                      <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                        Vous créez ici un compte <span className="font-semibold">vendeur</span>. Si vous souhaitez devenir affilié, vous pourrez activer votre espace dédié depuis votre tableau de bord.
                      </div>
                      <div>
                        <Label>Type de compte</Label>
                        <Select value={accountType} onValueChange={setAccountType}>
                          <SelectTrigger data-testid="account-type-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="particulier">Particulier</SelectItem>
                            <SelectItem value="societe">Société</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="reg-full-name">Nom complet</Label>
                        <Input
                          id="reg-full-name"
                          name="full_name"
                          data-testid="register-full-name"
                          value={formData.full_name}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      {accountType === 'societe' && (
                        <>
                          <div>
                            <Label htmlFor="company-name">Nom de la société</Label>
                            <Input
                              id="company-name"
                              name="company_name"
                              data-testid="register-company-name"
                              value={formData.company_name}
                              onChange={handleInputChange}
                              required
                            />
                          </div>
                          <div>
                            <Label htmlFor="company-reg">Numéro d'enregistrement</Label>
                            <Input
                              id="company-reg"
                              name="company_registration"
                              data-testid="register-company-reg"
                              value={formData.company_registration}
                              onChange={handleInputChange}
                              required
                            />
                          </div>
                        </>
                      )}

                      <div>
                        <Label htmlFor="phone">Téléphone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          data-testid="register-phone"
                          placeholder="+242..."
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div>
                        <Label>Pays</Label>
                        <Select 
                          value={formData.country} 
                          onValueChange={handleCountryChange}
                        >
                          <SelectTrigger data-testid="register-country-select">
                            <SelectValue placeholder="Sélectionnez votre pays" />
                          </SelectTrigger>
                          <SelectContent>
                            {countries.map((country) => (
                              <SelectItem key={country.code} value={country.code}>
                                {country.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>Ville</Label>
                        <Select 
                          value={formData.city} 
                          onValueChange={(val) => setFormData({ ...formData, city: val })}
                          disabled={!formData.country}
                        >
                          <SelectTrigger data-testid="register-city-select">
                            <SelectValue placeholder="Sélectionnez votre ville" />
                          </SelectTrigger>
                          <SelectContent>
                            {cities.map((city) => (
                              <SelectItem key={city.code} value={city.code}>
                                {city.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label htmlFor="reg-email">Email</Label>
                        <Input
                          id="reg-email"
                          name="email"
                          type="email"
                          data-testid="register-email"
                          value={formData.email}
                          onChange={handleInputChange}
                          required
                        />
                      </div>

                      <div>
                        <Label htmlFor="reg-password">Mot de passe</Label>
                        <div className="relative">
                          <Input
                            id="reg-password"
                            name="password"
                            type={showRegisterPassword ? 'text' : 'password'}
                            data-testid="register-password"
                            value={formData.password}
                            onChange={handleInputChange}
                            required
                            className="pr-10"
                          />
                          <button
                            type="button"
                            aria-label={showRegisterPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                            className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-gray-500 hover:text-gray-700"
                            data-testid="register-password-toggle"
                            onClick={() => setShowRegisterPassword((value) => !value)}
                          >
                            {showRegisterPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </div>

                      <div className="space-y-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-start space-x-3">
                          <Checkbox
                            id="privacy-policy"
                            checked={acceptedPrivacy}
                            onCheckedChange={setAcceptedPrivacy}
                            data-testid="privacy-checkbox"
                          />
                          <div className="flex-1">
                            <Label
                              htmlFor="privacy-policy"
                              className="text-sm font-normal cursor-pointer"
                            >
                              J'accepte la{' '}
                              <button
                                type="button"
                                onClick={() => window.open('/privacy', '_blank')}
                                className="text-cyan-600 hover:text-cyan-700 underline font-medium"
                              >
                                politique de confidentialité
                              </button>
                              {' '}et j'autorise Coli Noki à traiter mes données personnelles conformément à celle-ci.
                            </Label>
                          </div>
                        </div>
                        <div className="flex items-start space-x-3">
                          <Checkbox
                            id="terms-policy"
                            checked={acceptedTerms}
                            onCheckedChange={setAcceptedTerms}
                            data-testid="terms-checkbox"
                          />
                          <div className="flex-1">
                            <Label htmlFor="terms-policy" className="text-sm font-normal cursor-pointer">
                              J'accepte les{' '}
                              <button
                                type="button"
                                onClick={() => window.open('/terms', '_blank')}
                                className="text-cyan-600 hover:text-cyan-700 underline font-medium"
                              >
                                conditions d'utilisation
                              </button>.
                            </Label>
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 ml-7">
                          En cochant ces cases, vous acceptez la politique de confidentialité et les conditions d'utilisation.
                        </p>
                      </div>

                      <Button
                        type="submit"
                        data-testid="register-submit-btn"
                        className="w-full bg-cyan-500 hover:bg-cyan-600"
                        disabled={loading || !acceptedPrivacy || !acceptedTerms}
                      >
                        {loading ? 'Inscription...' : UI_AUTH_LABELS.registerAction}
                      </Button>

                      {accountType === 'societe' && (
                        <p className="text-sm text-amber-600 text-center">
                          Les comptes sociétés nécessitent une validation administrateur
                        </p>
                      )}
                    </form>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </div>

          <div className="w-full max-w-4xl" data-testid="landing-trustpilot-block">
            <Card className="overflow-hidden border border-emerald-100/80 bg-gradient-to-br from-white via-emerald-50/80 to-white shadow-[0_18px_60px_-40px_rgba(22,163,74,0.3)]">
              <CardContent className="p-6 sm:p-8">
                <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                  <div className="max-w-2xl space-y-3">
                    <p className="text-sm font-semibold uppercase tracking-[0.24em] text-emerald-600">Trustpilot</p>
                    <h3 className="text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
                      Votre avis compte
                    </h3>
                    <p className="text-base leading-7 text-slate-600">
                      Vous utilisez déjà Coli Noki ? Partagez votre expérience sur Trustpilot et aidez d’autres utilisateurs à nous faire confiance.
                    </p>
                  </div>

                  <div className="w-full lg:max-w-sm">
                    <div className="rounded-2xl border border-emerald-100 bg-white/95 p-4 shadow-sm" data-testid="trustpilot-widget-card">
                      <div
                        ref={trustpilotWidgetRef}
                        data-testid="trustpilot-widget-wrapper"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <FooterNew />
    </div>
  );
};

export default LandingPage;


